import { HubRichTextBackground, Maybe, RichTextParagraph } from '@/__generated__/graphql';
import { isBicentenary } from '@/lib/utils';
import { GridContainer, h1, h3, mediaQueries } from '@theme/lib';
import { ReactElement } from 'react';
import styled, { css, DefaultTheme, useTheme } from 'styled-components';
import WYSIWYG from '../WYSIWYG';
import {
  bicentenaryBackgroundClass,
  getBackgroundPatternProperties,
} from '@components/BicentenaryPatterns/util';
import {
  backgroundPattern,
} from '@components/BicentenaryPatterns/BicentenaryPatterns.styles';

export type GridContainerRichTextProps = {
  background?: Maybe<HubRichTextBackground>;
  field_remove_spacing?: boolean;
};

// Function to generate styling based on background prop
export const getBicentenaryRichTextStyling = (
  props: { theme: DefaultTheme } & GridContainerRichTextProps
) => {
  const backgroundProp = props.background;
  const patternProperties = getBackgroundPatternProperties(backgroundProp);

  const style = css`
    padding-top: 1.75rem;
    padding-bottom: 1.75rem;
    ${mediaQueries.md} {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }

    background-color: ${patternProperties.backgroundColor};
    ${patternProperties.backgroundImage ? backgroundPattern(patternProperties.backgroundImage) : ''}

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${patternProperties.headingColor};
      ${(props) => isBicentenary(props.theme.key) ? css`
        font-weight: 800;
        font-family: ${(props) => props.theme.fonts.h1};
      ` : ''}
    }

    th,
    td,
    li,
    p {
      color: ${patternProperties.textColor};
    }
    li,
    p {
      ${patternProperties.backgroundImage ? h3 : ''}
    }

    a {
      color: ${props.theme.colors.primary};
    }

    .btn-primary {
      color: ${patternProperties.button.primary.text};
      background-color: ${patternProperties.button.primary.bg};
      border-color: ${patternProperties.button.primary.border};
      transition: all 200ms linear;
      text-decoration: none;
      &:hover {
        background-color: ${patternProperties.button.primary.hoverBackground};
        border-color: ${patternProperties.button.primary.hoverBorder};
        color: ${patternProperties.button.primary.hoverText};
      }
      &:focus {
        text-decoration: none;
        border-color: ${patternProperties.button.primary.focusBorder};
      }
    }
    .btn-secondary {
      text-decoration: none;
      &:hover {
        background-color: ${patternProperties.button.secondary.hoverBackground} !important;
        border-color: ${patternProperties.button.secondary.hoverBorder} !important;
        color: ${patternProperties.button.secondary.hoverText} !important;
      }
      &:focus {
        text-decoration: none;
        border-color: ${patternProperties.button.secondary.focusBorder} !important;
      }
    }
  `;

  return style;
};

export const BicentenaryRichTextGridContainerRich = styled(
  GridContainer
).withConfig({
  shouldForwardProp: (prop) =>
    prop !== 'background' && prop !== 'field_remove_spacing',
})<GridContainerRichTextProps>`
  ${(props) => getBicentenaryRichTextStyling(props)}
  display: block;
  max-width: none;
  ${(props) => isBicentenary(props.theme.key) && props.field_remove_spacing === true ? css`
    margin-bottom: 0 !important; // this takes precedence over other rules.
    padding-bottom: 0 !important;
  ` : ``};

  > div {
    margin-bottom: 0;
    .wysiwyg {
      z-index: 10;
    }
  }
`;

export type RichTextProps = RichTextParagraph & {
  admin?: boolean;
  textAlign?: string;
};

export default function RichText(paragraph: RichTextProps): ReactElement {
  const theme = useTheme();

  if (isBicentenary(theme.key)) {
    return (
      <BicentenaryRichTextGridContainerRich
        className={`${paragraph.__typename} ${bicentenaryBackgroundClass(paragraph)}`}
        key={paragraph.id}
        background={paragraph.field_hub_rich_text_background}
        field_remove_spacing={Boolean(paragraph.field_remove_spacing)}
      >
        <GridContainer>
          <WYSIWYG {...paragraph} />
        </GridContainer>
      </BicentenaryRichTextGridContainerRich>
    );
  }

  return (
    <GridContainer className={paragraph.__typename} key={paragraph.id}>
      <WYSIWYG {...paragraph} />
    </GridContainer>
  );
}
